import React, { useContext } from "react"
import { isCompleteStatus, getRequirementDisplayData } from "./utils"
import { resolveUrlForArticles } from "./utils/urlUtils"
import { REQUIREMENT, PAT_WARNING_MESSAGES } from "./utils/constants/levels"
import { STATUS } from "./utils/constants/statuses"
import { GPTP_MESSAGE_VARIANT } from "./utils/suspensionUtils"
import { ADDITIONAL_REQUIREMENTS } from "./utils/constants/additionalRequirements"
import { resolveSeminarViewMyEventsUrl } from "./utils/constants/externalUrls"
import { resolveBackgroundCheckCta } from "../../../utils/prospect/backgroundCheckCta"
import RequirementActionLinks from "./RequirementActionLinks"
import ApplicationDenialMessage from "../../PtmApplicationStatus/ApplicationDenialMessage"
import MemberApplicationElectionDate from "../MemberApplicationElectionDate"
import { resolveEnrollmentDenial } from "./utils/enrollmentDenial"
import {
  isLatestMemberApplicationAwaitingSection,
  SECTION_EMPLOYMENT_VERIFICATION_LABEL,
  SECTION_EMPLOYMENT_VERIFICATION_MESSAGE,
} from "../../../utils/sectionEmploymentVerification"
import { flags, useFeatureFlags } from "../../../lib/feature-flags"
import LockIconSvg from "../../../assets/icons/icon-lock-status.svg"
import MembershipJourneyContext from "./MembershipJourneyContext"
import {
  RequirementCardStyled,
  RequirementHeader,
  RequirementTitle,
  StatusBadgeSmall,
  StatusBadgeText,
  StatusBadgeCheckmark,
  StatusBadgeLock,
  RequirementDescription,
  RequirementInfoBanner,
  RequirementInfoText,
  RequirementDenialMessage,
  TextButton,
} from "./Styles/ProgressTracker.styles"

// pga-backend reports `complete` for an approved Member application (and for
// elected Members); `approved` only comes from the prototype catalog. The
// election date component renders nothing unless the latest application is
// approved, not yet elected, and has a date.
const ENROLLMENT_ELECTION_STATUSES = new Set([STATUS.APPROVED, STATUS.COMPLETE])

const RequirementCard = ({
  requirement: payloadRequirement,
  activeLevel,
  shouldShowPATWarning,
}) => {
  // Optional catalog overlay (prototype); default production catalog when absent
  const context = useContext(MembershipJourneyContext)
  const [applicationsMembershipEnabled, denialMessageEnabled] =
    useFeatureFlags([
      flags.FLAG_FEAT_APPLICATIONS_MEMBERSHIP,
      flags.FLAG_FEAT_APPLICATIONS_DENIAL_MESSAGE,
    ]) ?? []
  const deniedEnrollmentApplication = resolveEnrollmentDenial({
    requirement: payloadRequirement,
    memberApplications: context?.memberApplications,
    applicationsMembershipEnabled,
  })
  const requirement = deniedEnrollmentApplication
    ? { ...payloadRequirement, status: STATUS.DENIED }
    : payloadRequirement
  const baseDisplayData = getRequirementDisplayData(
    activeLevel,
    requirement,
    context?.requirementDataCatalog,
    {
      useAcceptableProgressTerminatedCopy:
        context?.gptpMessageVariant === GPTP_MESSAGE_VARIANT.TERMINATED,
      acceptableProgressPhase: context?.progressStanding?.phase,
    },
  )
  const isComplete = isCompleteStatus(requirement.status)
  const showLockIcon = requirement.status === STATUS.LOCKED
  const isPATWithWarning =
    requirement.key === REQUIREMENT.PLAYING_ABILITY_TEST && shouldShowPATWarning

  const backgroundCheckOrder = context?.me?.prospect?.backgroundCheckOrder
  const backgroundCheckCta =
    requirement.key === REQUIREMENT.BACKGROUND_CHECK
      ? resolveBackgroundCheckCta(backgroundCheckOrder)
      : null

  let displayData =
    isPATWithWarning && baseDisplayData
      ? {
          ...baseDisplayData,
          secondaryLinkText: ADDITIONAL_REQUIREMENTS.PAT.secondaryLinkText,
          secondaryLinkUrl: ADDITIONAL_REQUIREMENTS.PAT.secondaryLinkUrl,
        }
      : baseDisplayData

  if (backgroundCheckCta?.action === "hire_right" && displayData) {
    displayData = {
      ...displayData,
      primaryLinkText: backgroundCheckCta.label,
      primaryLinkUrl: backgroundCheckCta.url,
    }
  }

  if (
    requirement.key === REQUIREMENT.ENROLLMENT &&
    requirement.status === STATUS.SUBMITTED &&
    displayData &&
    isLatestMemberApplicationAwaitingSection(
      context?.memberApplications,
      context?.memberApplicationsAwaitingSection,
    )
  ) {
    displayData = {
      ...displayData,
      label: SECTION_EMPLOYMENT_VERIFICATION_LABEL,
      message: SECTION_EMPLOYMENT_VERIFICATION_MESSAGE,
    }
  }

  if (
    requirement.key === REQUIREMENT.VIRTUAL_SEMINAR &&
    requirement.status === STATUS.REGISTERED
  ) {
    displayData = {
      ...displayData,
      secondaryLinkUrl: resolveSeminarViewMyEventsUrl(requirement.seminarInfo),
    }
  }

  return (
    <RequirementCardStyled hasWarning={isPATWithWarning}>
      {isPATWithWarning && (
        <RequirementInfoBanner>
          <RequirementInfoText>
            {PAT_WARNING_MESSAGES.BANNER}{" "}
            {displayData?.infoLinkText && displayData?.infoLinkUrl && (
              <TextButton
                as="a"
                href={resolveUrlForArticles(displayData.infoLinkUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {displayData.infoLinkText}
              </TextButton>
            )}
          </RequirementInfoText>
        </RequirementInfoBanner>
      )}
      <RequirementHeader>
        <RequirementTitle>
          {displayData?.name || requirement.name}
        </RequirementTitle>
        <StatusBadgeSmall backgroundColor={displayData?.backgroundColor}>
          {isComplete && <StatusBadgeCheckmark color={displayData?.color} />}
          {showLockIcon && <StatusBadgeLock src={LockIconSvg} alt="Locked" />}
          <StatusBadgeText textColor={displayData?.color}>
            {displayData?.label || requirement.status}
          </StatusBadgeText>
        </StatusBadgeSmall>
      </RequirementHeader>
      <RequirementDescription>
        {isPATWithWarning
          ? PAT_WARNING_MESSAGES.DESCRIPTION
          : displayData?.message || ""}
        {!isPATWithWarning &&
          displayData?.infoLinkText &&
          displayData?.infoLinkUrl && (
            <>
              {" "}
              <TextButton
                as="a"
                href={resolveUrlForArticles(displayData.infoLinkUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {displayData.infoLinkText}
              </TextButton>
            </>
          )}
      </RequirementDescription>
      {requirement.key === REQUIREMENT.ENROLLMENT &&
      ENROLLMENT_ELECTION_STATUSES.has(requirement.status) ? (
        <MemberApplicationElectionDate />
      ) : null}
      {deniedEnrollmentApplication && denialMessageEnabled ? (
        <RequirementDenialMessage>
          <ApplicationDenialMessage
            applicationId={deniedEnrollmentApplication.id}
            applicationType={deniedEnrollmentApplication.applicationType}
          />
        </RequirementDenialMessage>
      ) : null}
      <RequirementActionLinks
        displayData={displayData}
        requirementStatus={requirement.status}
        activeLevel={activeLevel}
        requirementKey={requirement.key}
      />
    </RequirementCardStyled>
  )
}

export default RequirementCard
